import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import {
  AccordionLevelsSection,
  BadgeCard,
  BadgeEarned,
  BadgesContainer,
  BadgesGrid,
  Container,
  CurrentLevelIcon,
  DescriptionBadge,
  DescriptionLevel,
  ExtraVerificationWrapper,
  Fill,
  LevelCard,
  LevelCardsGrid,
  LevelContainer,
  LevelDisplay,
  LevelProgressBar,
  LevelProgressContainer,
  LevelTitle,
  NextLevelIcon,
  NextLevelRequiriments,
  NextLevelWrapper,
  PointsLabel,
  Title,
  TodoContent,
  TodoDescription,
  TodoItem,
  TodoList,
  TodoProgress,
} from './styles';
import { MedalIcon, ShieldHeartIcon } from '../../../assets/svg/SVGComponents';
import { useAuth } from '../../../hooks/auth';
import api from '../../../services/api';
import { IUserGamificationProfileDTO } from '../../../dtos/IGamificationDataDTO';
import { ILevelDTO } from '../../../dtos/ILevelDTO';
import { IBadgeDTO } from '../../../dtos/IBadgeDTO';
import AccordionProfile from '../../../components/AccordionProfile';
import LoadingState from './LoadingState';

const ProfileBadges: React.FC = () => {
  const { user } = useAuth();
  const [gamificationData, setGamificationData] =
    React.useState<IUserGamificationProfileDTO | null>();
  const [badgeData, setBadgeData] = React.useState<IBadgeDTO[] | null>(null);
  const [levelData, setLevelData] = React.useState<ILevelDTO[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (isTabletOrMobile || isPortrait) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [isPortrait, isTabletOrMobile]);
  useEffect(() => {
    const fetchProfileData = async (): Promise<void> => {
      const response = await api.get(
        `/gamification/users/${user.id}/gamification/profile`,
      );
      setGamificationData(response.data);
      setLoading(false);
    };
    fetchProfileData();
  }, [user.id]);

  useEffect(() => {
    const fetchLevels = async (): Promise<void> => {
      const response = await api.get<ILevelDTO[]>('/levels/list');
      setLevelData(response.data);
    };
    fetchLevels();
  }, []);

  useEffect(() => {
    const fetchLevels = async (): Promise<void> => {
      const response = await api.get<IBadgeDTO[]>('/badges/list');
      setBadgeData(response.data);
    };
    fetchLevels();
  }, []);

  const progress = React.useMemo(() => {
    if (
      gamificationData?.current_level &&
      gamificationData.current_level.max_points
    ) {
      return Math.min(
        (gamificationData.total_points /
          gamificationData.current_level.max_points) *
          100,
        100,
      );
    }
    return 0;
  }, [gamificationData]);

  return (
    <>
      {!!loading && <LoadingState />}
      {!loading && gamificationData && (
        <Container className="profile-badges-container">
          <LevelContainer>
            <Title>
              <MedalIcon width="1.7rem" strokeWidth={1} />
              <span>Nível</span>
            </Title>

            <LevelDisplay>
              <CurrentLevelIcon
                src={gamificationData.current_level?.icon_url ?? ''}
                alt="current-level"
              />
              <LevelProgressContainer $displayMode="full">
                <LevelTitle>
                  Nv. {gamificationData.current_level?.name}
                </LevelTitle>
                <LevelProgressBar>
                  <Fill $progress={progress} />
                </LevelProgressBar>
                <PointsLabel>
                  {gamificationData.total_points} pontos
                </PointsLabel>
              </LevelProgressContainer>
              <NextLevelWrapper>
                <NextLevelIcon
                  src={gamificationData.next_level?.icon_url ?? ''}
                  alt="next-level"
                />
                {!isMobile && (
                  <NextLevelRequiriments>
                    <h1>Próximo nível: {gamificationData.next_level?.name}</h1>
                    <p>
                      pontos necessários:{' '}
                      {gamificationData.next_level?.points_needed} pontos
                    </p>
                    {gamificationData.next_level?.verification && (
                      <ExtraVerificationWrapper>
                        <span>Verificação extra:</span>
                        <TodoList>
                          {/* Completed requirements */}
                          {gamificationData.next_level?.verification?.completed_requirements.map(
                            requirement => (
                              <TodoItem key={requirement.type} $isCompleted>
                                <span className="checkmark">✓</span>
                                <TodoContent>
                                  {requirement.description && (
                                    <TodoDescription>
                                      {requirement.description}
                                    </TodoDescription>
                                  )}
                                </TodoContent>
                              </TodoItem>
                            ),
                          )}

                          {/* Pending requirements */}
                          {gamificationData.next_level?.verification?.pending_requirements.map(
                            requirement => (
                              <TodoItem
                                key={requirement.type}
                                $isCompleted={false}
                              >
                                <span className="pending">○</span>
                                <TodoContent>
                                  {requirement.description && (
                                    <TodoDescription>
                                      {requirement.description}
                                    </TodoDescription>
                                  )}
                                  <TodoProgress>
                                    {requirement.current}/{requirement.required}
                                  </TodoProgress>
                                </TodoContent>
                              </TodoItem>
                            ),
                          )}
                        </TodoList>
                      </ExtraVerificationWrapper>
                    )}
                  </NextLevelRequiriments>
                )}
              </NextLevelWrapper>
            </LevelDisplay>

            {isMobile && (
              <NextLevelRequiriments>
                <h1>Próximo nível: {gamificationData.next_level?.name}</h1>
                <p>
                  pontos necessários:{' '}
                  {gamificationData.next_level?.points_needed} pontos
                </p>
                {gamificationData.next_level?.verification && (
                  <ExtraVerificationWrapper>
                    <span>Verificação extra:</span>
                    <TodoList>
                      {/* Completed requirements */}
                      {gamificationData.next_level?.verification?.completed_requirements.map(
                        requirement => (
                          <TodoItem key={requirement.type} $isCompleted>
                            <span className="checkmark">✓</span>
                            <TodoContent>
                              {requirement.description && (
                                <TodoDescription>
                                  {requirement.description}
                                </TodoDescription>
                              )}
                            </TodoContent>
                          </TodoItem>
                        ),
                      )}

                      {/* Pending requirements */}
                      {gamificationData.next_level?.verification?.pending_requirements.map(
                        requirement => (
                          <TodoItem key={requirement.type} $isCompleted={false}>
                            <span className="pending">○</span>
                            <TodoContent>
                              {requirement.description && (
                                <TodoDescription>
                                  {requirement.description}
                                </TodoDescription>
                              )}
                              <TodoProgress>
                                {requirement.current}/{requirement.required}
                              </TodoProgress>
                            </TodoContent>
                          </TodoItem>
                        ),
                      )}
                    </TodoList>
                  </ExtraVerificationWrapper>
                )}
              </NextLevelRequiriments>
            )}
            <AccordionLevelsSection>
              {levelData && (
                <AccordionProfile title="Ver próximos níveis" open={false}>
                  <LevelCardsGrid>
                    {levelData?.map(level => (
                      <LevelCard key={level.id}>
                        <img src={level.icon_url ?? ''} alt={level.name} />
                        <DescriptionLevel>
                          <h2>{level.name}</h2>
                          <p>{level.description}</p>
                          <span>
                            Requisitos: {level.required_points} pontos
                          </span>
                          <span>
                            Máximo de pontos: {level.max_points ?? 'Sem limite'}{' '}
                            pontos
                          </span>
                        </DescriptionLevel>
                      </LevelCard>
                    ))}
                  </LevelCardsGrid>
                </AccordionProfile>
              )}
            </AccordionLevelsSection>
          </LevelContainer>

          <BadgesContainer>
            <Title>
              <ShieldHeartIcon
                width="1.7rem"
                stroke="#323232"
                strokeWidth={1.2}
              />
              <span>Emblemas</span>
            </Title>
            <BadgesGrid>
              {gamificationData?.badges.length ? (
                gamificationData.badges.map(badge => (
                  <BadgeEarned key={badge.id}>
                    <img src={badge.icon_url ?? ''} alt={badge.name} />
                    <h2>{badge.name}</h2>
                    <p>{badge.description}</p>
                    <span>
                      Conquistada em:{' '}
                      {new Date(badge.awarded_at).toLocaleDateString()}
                    </span>
                  </BadgeEarned>
                ))
              ) : (
                <p>Nenhuma conquista ainda.</p>
              )}
            </BadgesGrid>
            <AccordionProfile title="Ver todos emblemas" open={false}>
              <BadgesGrid>
                {badgeData?.map(badge => (
                  <BadgeCard key={badge.id}>
                    <img src={badge.icon_url ?? ''} alt={badge.name} />
                    <h2>{badge.name}</h2>
                    <DescriptionBadge>
                      <p>{badge.description}</p>
                    </DescriptionBadge>
                  </BadgeCard>
                ))}
              </BadgesGrid>
            </AccordionProfile>
          </BadgesContainer>
        </Container>
      )}
    </>
  );
};

export default ProfileBadges;
